import React, { useCallback, useMemo, useState } from 'react';

import { SPECIAL_ACTIONS } from 'model/specialActions';
import type { WorkflowEdge, WorkflowEdgeData, WorkflowNode } from 'model/xyflowMapping';
import Styled from './ConnectionsList.styled';

const {
  StyledSection,
  StyledSectionLabel,
  StyledMuted,
  StyledEdgeCard,
  StyledEdgeFrom,
  StyledRow,
  StyledMutedLabel,
  StyledInlineLabel,
  StyledSelect,
  StyledRemoveButton,
  StyledAddWrapper,
} = Styled;

interface OwnProps {
  node: WorkflowNode;
  allNodes: WorkflowNode[];
  allEdges: WorkflowEdge[];
  onEdgeDataChange: (edgeId: string, data: Partial<WorkflowEdgeData>) => void;
  onDeleteEdge: (edgeId: string) => void;
  onAddEdge: (sourceId: string, targetId: string) => void;
}

interface EdgeRowProps {
  edge: WorkflowEdge;
  sourceNode: WorkflowNode | undefined;
  onEdgeDataChange: (edgeId: string, data: Partial<WorkflowEdgeData>) => void;
  onDeleteEdge: (edgeId: string) => void;
}

/** A single row in the connection list. Inlined here because the row is only used by ConnectionsList. */
const EdgeRow = ({ edge, sourceNode, onEdgeDataChange, onDeleteEdge }: EdgeRowProps) => {
  const isSourceRoute = sourceNode?.data.actionId === SPECIAL_ACTIONS.route;
  const sourceRoutes = useMemo<Array<{ key: string }>>(() => {
    if (!isSourceRoute) return [];
    const routes = sourceNode?.data.settings.Routes;
    return Array.isArray(routes) ? (routes as Array<{ key: string }>) : [];
  }, [isSourceRoute, sourceNode]);

  const handleRouteKeyChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      onEdgeDataChange(edge.id, { routeKey: event.target.value || undefined });
    },
    [edge.id, onEdgeDataChange],
  );

  const handleEnableOnConditionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onEdgeDataChange(edge.id, { enableOnCondition: event.target.checked });
    },
    [edge.id, onEdgeDataChange],
  );

  const handleDelete = useCallback(() => {
    onDeleteEdge(edge.id);
  }, [edge.id, onDeleteEdge]);

  return (
    <StyledEdgeCard>
      <StyledEdgeFrom>From: {edge.source}</StyledEdgeFrom>
      {isSourceRoute && sourceRoutes.length > 0 && (
        <StyledRow>
          <StyledMutedLabel htmlFor={`route-key-${edge.id}`}>Route key</StyledMutedLabel>
          <StyledSelect
            id={`route-key-${edge.id}`}
            value={edge.data?.routeKey ?? ''}
            onChange={handleRouteKeyChange}
          >
            <option value=''>None</option>
            {sourceRoutes.map((r) => (
              <option key={r.key} value={r.key}>
                {r.key}
              </option>
            ))}
          </StyledSelect>
        </StyledRow>
      )}
      {isSourceRoute && (
        <StyledInlineLabel>
          <input
            type='checkbox'
            checked={edge.data?.enableOnCondition ?? false}
            onChange={handleEnableOnConditionChange}
          />
          Enable on condition
        </StyledInlineLabel>
      )}
      <StyledRemoveButton type='button' onClick={handleDelete} className='btn-ghost btn-sm'>
        Remove
      </StyledRemoveButton>
    </StyledEdgeCard>
  );
};

const MemoEdgeRow = React.memo(EdgeRow);

const ConnectionsList = ({
  node,
  allNodes,
  allEdges,
  onEdgeDataChange,
  onDeleteEdge,
  onAddEdge,
}: OwnProps) => {
  const [addConnectionSource, setAddConnectionSource] = useState<string>('');

  const incomingEdges = useMemo(
    () => allEdges.filter((e) => e.target === node.id && e.data?.kind === 'control'),
    [allEdges, node.id],
  );

  const availableSources = useMemo(() => {
    const connectedSourceIds = new Set(incomingEdges.map((e) => e.source));
    return allNodes.filter((n) => n.id !== node.id && !connectedSourceIds.has(n.id));
  }, [allNodes, incomingEdges, node.id]);

  const handleAddConnection = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const sourceId = event.target.value;
      if (sourceId) {
        onAddEdge(sourceId, node.id);
        setAddConnectionSource('');
      }
    },
    [onAddEdge, node.id],
  );

  return (
    <StyledSection>
      <StyledSectionLabel>Connections (Run After)</StyledSectionLabel>
      {incomingEdges.length === 0 && <StyledMuted>No incoming connections.</StyledMuted>}
      {incomingEdges.map((edge) => (
        <MemoEdgeRow
          key={edge.id}
          edge={edge}
          sourceNode={allNodes.find((n) => n.id === edge.source)}
          onEdgeDataChange={onEdgeDataChange}
          onDeleteEdge={onDeleteEdge}
        />
      ))}
      {availableSources.length > 0 && (
        <StyledAddWrapper>
          <StyledSelect
            value={addConnectionSource}
            onChange={handleAddConnection}
            aria-label='Add connection'
          >
            <option value=''>Add connection...</option>
            {availableSources.map((n) => (
              <option key={n.id} value={n.id}>
                {n.id}
              </option>
            ))}
          </StyledSelect>
        </StyledAddWrapper>
      )}
    </StyledSection>
  );
};

export default React.memo(ConnectionsList);
